import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { currencyService } from '../services/currencyService';

const KlarnaSection = () => {
  const [, setCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    const unsubscribe = currencyService.subscribe((nextCountry) => {
      setCountry(nextCountry);
    });

    return unsubscribe;
  }, []);

  const showKlarna = currencyService.showKlarna();
  const formatPrice = (price: number) => currencyService.formatPrice(price);
  const total = 1800;
  const installment = currencyService.getKlarnaInstallmentAmount(total);

  if (!showKlarna) {
    return null;
  }

  return (
    <section className="py-20 relative overflow-hidden bg-gradient-to-b from-white via-neon-orange/5 to-white">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute top-20 right-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-4"
            >
              <motion.div
                className="relative bg-white p-4 rounded-xl shadow-lg"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/9/9a/Klarna_Logo_black.svg"
                  alt="Klarna"
                  className="w-32 h-auto"
                />
                <div className="absolute inset-0 bg-neon-orange/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
              <span className="text-neon-orange font-bold text-xl px-4 py-1 bg-neon-orange/10 rounded-full">
                PARTENAIRE OFFICIEL
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl font-bold leading-tight"
            >
              Klarna reste une option
              <span className="bg-gradient-to-r from-neon-orange to-red-600 bg-clip-text text-transparent"> sur projets eligibles</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-gray-600 text-lg leading-relaxed"
            >
              Pour certains projets eligibles, Klarna permet un paiement en 3 fois sans frais. Nous ne le forcons pas: au-dela de {currencyService.getKlarnaMaxAmountEUR()} EUR, nous basculons sur un paiement unique, un devis ou une solution de financement plus adaptee.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/klarna-info"
                className="bg-gradient-to-r from-neon-orange to-red-600 text-white px-8 py-4 rounded-full font-semibold relative overflow-hidden group"
              >
                <span className="relative z-10">Découvrir Klarna</span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "100%" }}
                  transition={{ duration: 1 }}
                />
              </Link>

              <motion.div
                className="flex items-center gap-2 text-gray-600"
                whileHover={{ x: 5 }}
              >
                <svg className="w-6 h-6 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Sans frais cachés
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right Content - Payment Steps */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
              <div className="space-y-6">
                {[
                  { step: 1, text: "Klarna apparait si le projet est eligible" },
                  { step: 2, text: "Vous choisissez librement entre paiement complet et Klarna" },
                  { step: 3, text: "Validation du dossier en quelques instants" },
                  { step: 4, text: "3 versements egaux sans frais jusqu a 2000 EUR" }
                ].map((item, index) => (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, x: 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-center gap-4 group"
                  >
                    <div className="w-12 h-12 rounded-full bg-neon-orange/10 flex items-center justify-center text-neon-orange font-bold group-hover:bg-neon-orange group-hover:text-white transition-all duration-300">
                      {item.step}
                    </div>
                    <div className="text-gray-600 group-hover:text-gray-900 transition-colors duration-300">
                      {item.text}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                className="mt-8 p-4 bg-neon-orange/5 rounded-xl border border-neon-orange/20"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="text-sm text-gray-600">
                  Exemple pour un achat de {formatPrice(total)} :
                  <ul className="mt-2 space-y-1">
                    <li>• Premier paiement : {formatPrice(installment)}</li>
                    <li>• 2 mensualités de : {formatPrice(installment)}</li>
                    <li>• Coût total : {formatPrice(total)} (TAEG 0%)</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default KlarnaSection;
